import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, getToken, setToken, clearToken } from "@/lib/api";
import { can as _can } from "@/lib/perms";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null); setTenant(null); setLoading(false); return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      clearToken();
      setUser(null); setTenant(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user); setTenant(data.tenant);
    return data.user;
  }, []);

  const register = useCallback(async (name, organization_name, email, password) => {
    const { data } = await api.post("/auth/register", { name, organization_name, email, password });
    setToken(data.token);
    setUser(data.user); setTenant(data.tenant);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null); setTenant(null);
  }, []);

  const can = useCallback((action) => _can(user?.role, action), [user?.role]);

  const value = useMemo(
    () => ({ user, tenant, loading, login, register, logout, refreshMe, can }),
    [user, tenant, loading, login, register, logout, refreshMe, can]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
